import type { Cart, CartLine } from '@repo/shopify';
import { type AnalyticsCart, type AnalyticsCartLine, AnalyticsEvent } from '@shopify/hydrogen';

/**
 * Publishes confirmed cart changes to Shopify's analytics bus
 * (`window.Shopify.analytics`, installed by ShopifyScripts), which forwards
 * them to Shopify's own destination and gates them on Shopify consent.
 *
 * Hydrogen's `trackCartAnalytics()` derives these events by diffing a cart
 * store, but our CartProvider loads the cart lazily, so it often has no
 * baseline and would report every existing line as a new add. Line pairing
 * here mirrors the provider's own analytics diff instead: by line id when a
 * baseline exists, by requested variant when it doesn't.
 */

export interface ShopifyCartLineChange {
	type: 'add' | 'remove';
	currentLine?: AnalyticsCartLine;
	prevLine?: AnalyticsCartLine;
}

interface RequestedLine {
	merchandiseId: string;
	quantity: number;
}

export function toShopifyAnalyticsCart(cart: Cart): AnalyticsCart {
	return {
		id: cart.id,
		updatedAt: cart.updatedAt,
		cost: cart.cost,
		lines: { nodes: cart.lines.edges.map((edge) => toShopifyAnalyticsLine(edge.node)) },
	};
}

export function buildShopifyCartLineChanges(
	previousCart: Cart | null,
	cart: Cart,
	requestedLines: RequestedLine[] | undefined,
): ShopifyCartLineChange[] {
	return previousCart
		? diffCartLines(previousCart, cart)
		: buildChangesFromRequestedLines(cart, requestedLines);
}

export function publishShopifyCartChange({
	previousCart,
	cart,
	requestedLines,
}: {
	previousCart: Cart | null;
	cart: Cart;
	requestedLines: RequestedLine[] | undefined;
}) {
	const analytics = typeof window === 'undefined' ? undefined : window.Shopify?.analytics;
	if (!analytics) return;

	const changes = buildShopifyCartLineChanges(previousCart, cart, requestedLines);
	if (changes.length === 0) return;

	const payload = {
		cart: toShopifyAnalyticsCart(cart),
		prevCart: previousCart ? toShopifyAnalyticsCart(previousCart) : null,
	};

	analytics.publish(AnalyticsEvent.CART_UPDATED, payload);
	for (const change of changes) {
		analytics.publish(
			change.type === 'add'
				? AnalyticsEvent.PRODUCT_ADD_TO_CART
				: AnalyticsEvent.PRODUCT_REMOVED_FROM_CART,
			{ ...payload, currentLine: change.currentLine, prevLine: change.prevLine },
		);
	}
}

function diffCartLines(previousCart: Cart, cart: Cart): ShopifyCartLineChange[] {
	const changes: ShopifyCartLineChange[] = [];
	const currentLines = new Map(cart.lines.edges.map((edge) => [edge.node.id, edge.node]));
	const previousIds = new Set<string>();

	for (const { node: previousLine } of previousCart.lines.edges) {
		previousIds.add(previousLine.id);
		const currentLine = currentLines.get(previousLine.id);
		const prevLine = toShopifyAnalyticsLine(previousLine);

		if (!currentLine) {
			changes.push({ type: 'remove', prevLine });
		} else if (previousLine.quantity !== currentLine.quantity) {
			changes.push({
				type: previousLine.quantity < currentLine.quantity ? 'add' : 'remove',
				prevLine,
				currentLine: toShopifyAnalyticsLine(currentLine),
			});
		}
	}

	for (const { node: currentLine } of cart.lines.edges) {
		if (!previousIds.has(currentLine.id)) {
			changes.push({ type: 'add', currentLine: toShopifyAnalyticsLine(currentLine) });
		}
	}

	return changes;
}

function buildChangesFromRequestedLines(
	cart: Cart,
	requestedLines: RequestedLine[] | undefined,
): ShopifyCartLineChange[] {
	if (!requestedLines?.length) return [];

	const requestedQuantities = new Map<string, number>();
	for (const line of requestedLines) {
		requestedQuantities.set(
			line.merchandiseId,
			(requestedQuantities.get(line.merchandiseId) ?? 0) + line.quantity,
		);
	}

	const changes: ShopifyCartLineChange[] = [];
	for (const { node } of cart.lines.edges) {
		const requestedQuantity = requestedQuantities.get(node.merchandise.id);
		if (!requestedQuantity) continue;

		requestedQuantities.delete(node.merchandise.id);
		const currentLine = toShopifyAnalyticsLine(node);
		const previousQuantity = node.quantity - Math.min(requestedQuantity, node.quantity);
		changes.push({
			type: 'add',
			currentLine,
			// Without a baseline, a line that already held units is reported as a
			// quantity increase rather than a brand-new line.
			prevLine: previousQuantity > 0 ? { ...currentLine, quantity: previousQuantity } : undefined,
		});
	}

	return changes;
}

function toShopifyAnalyticsLine(line: CartLine): AnalyticsCartLine {
	const { merchandise } = line;
	return {
		id: line.id,
		quantity: line.quantity,
		merchandise: {
			id: merchandise.id,
			title: merchandise.title,
			price: line.cost.amountPerQuantity,
			product: {
				id: merchandise.product.id,
				title: merchandise.product.title,
				vendor: merchandise.product.vendor,
				productType: merchandise.product.productType || undefined,
				handle: merchandise.product.handle,
			},
		},
	};
}
